import React from 'react';

import { Button } from '@vlabs/uikit/components/controls/button/Button';
import { Radio } from '@vlabs/uikit/components/controls/radio/Radio';
import type { WithDataTestid } from '@vlabs/uikit/types';
import { useForm } from 'react-hook-form';

import { buildTestid } from '@vlabs/monokit/shared/lib/utils';

import st from './DeleteListsForm.module.sass';
import { useDeleteListsFormTranslation } from './i18n';
import type { ListId as PointListId } from '@vlabs/api/artemy/model';
import type { ListId as LunaListId } from '@vlabs/api/luna-api/model';

export type DeleteListsAction = 'delete_content' | 'detach_content';

export interface DeleteListsFormValues {
  // eslint-disable-next-line @typescript-eslint/no-duplicate-type-constituents
  listIds: PointListId[] | LunaListId[];
  action: DeleteListsAction;
}

export interface DeleteListsFormProps extends WithDataTestid {
  listIds: string[];
  onCancel?: VoidFunction;
  onSubmit: (values: DeleteListsFormValues) => void;
}

const displayName = 'DeleteListsForm';
const testid = buildTestid(displayName);

function DeleteListsForm({
  listIds,
  onSubmit,
  onCancel = undefined,
  'data-testid': dataTestId = undefined,
}: DeleteListsFormProps): React.JSX.Element {
  const { t } = useDeleteListsFormTranslation();
  const form = useForm<DeleteListsFormValues>({
    defaultValues: {
      listIds,
      action: 'delete_content',
    },
  });

  return (
    <form
      className={st.Root}
      onSubmit={form.handleSubmit(onSubmit)}
      {...testid(dataTestId)}
    >
      <header className={st.Header}>
        <h5>{t('title')}</h5>
        <span className={st.Subtitle}>
          {t('subtitle')}
        </span>
      </header>

      <div className={st.Content}>
        <Radio
          id="deleteListsDeleteContent"
          label={t('options.delete_content') as unknown as string}
          value="delete_content"
          {...form.register('action')}
          {...testid('Radio', 'DeleteContent', dataTestId)}
        />

        <Radio
          id="deleteListsDetachContent"
          label={t('options.detach_content') as unknown as string}
          value="detach_content"
          {...form.register('action')}
          {...testid('Radio', 'DetachContent', dataTestId)}
        />
      </div>

      <div className={st.Actions}>
        <Button
          className={st.Button}
          kind="negative"
          type="submit"
          {...testid('SubmitButton', dataTestId)}
        >
          {t('actions.delete')}
        </Button>

        <Button
          className={st.Button}
          kind="secondary"
          onClick={onCancel}
          type="button"
          variant="outlined"
          {...testid('CancelButton', dataTestId)}
        >
          {t('actions.cancel')}
        </Button>
      </div>
    </form>
  );
}

DeleteListsForm.displayName = displayName;

export { DeleteListsForm };
